import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ErrorHandler, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, Select, ofActionCompleted, ofActionSuccessful } from '@ngxs/store';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngstack/translate';
import * as utils from '@koomzo/commonutil';
import { LogIn, UpdatePushToken } from '@common/auth/store/auth.actions';
import { isExpectedSignInRefusal, signInErrorCode } from '@common/auth/models/sign-in-refusal';
import { UserRoleBasedRedirectService } from '@common/auth/services/user-rolebased-redirect.service';
import { BaseFormComponent } from '@shared/components/core/base-form.component';
import { KzLanguageOption } from '@shared/kz-ui';
import { AppState } from '@shared/store/app.state';
import {
  ChangeAppLang,
  SetCurrentTenant,
  SetCurrentTenantSupportedLanguages,
  SetLocalizationSettings
} from '@shared/store/app.actions';

/** EN / FR, as the DS's AUTH_LANGUAGES. */
export const AUTH_LANGUAGES: readonly KzLanguageOption[] = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' }
];

/**
 * `/login`: the entry point of the app (kz2 F1). `''` redirects here, and a restored session is
 * bounced to its role home by `AuthedHomeRedirectGuard`.
 *
 * The LAYOUT is the design system's (koomzodesignsystem `features/auth/pages/login`, specs/auth.md
 * §5.2): the slot-based `kz-auth-shell`, the brand lockup twice (appbar below the split, pitch
 * above it), the pitch, the offline reassurance, and the dock.
 *
 * The BEHAVIOUR is koomzoapps' own. The DS screen is a documented no-op, because it has no auth
 * to call. Here:
 * - the form is a dynamic-forms model (`CommonFormModels.getLoginFormModel`: phone plus dial code,
 *   revealable password);
 * - the dial code sets the localization settings;
 * - `LogIn` goes through `AuthState` → `AUTH_GATEWAY_PORT`;
 * - success updates the push token, loads the tenant, and follows `?return=` or the role redirect.
 *
 * Two deliberate differences from the DS:
 * - "Forgot password?" opens the real reset page, where the DS shows a notice that no reset flow
 *   exists.
 * - A failed sign-in is an inline `kz-inline-alert` (`role="alert"`), not a modal. It uses the
 *   existing `app.pages.login.*` failure copy and never says which of the two fields was wrong.
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LoginPage extends BaseFormComponent<any> implements OnInit {
  readonly languages = AUTH_LANGUAGES;

  @Select(AppState.AppLang)
  lang$: Observable<string>;

  /** True after a rejected sign-in, until the next attempt. */
  loginFailed = false;
  /**
   * kz2 M12 (R7): the rejection was a SUSPENDED account (`auth/user-disabled` — Firebase's code, and the mock
   * gateway's). Said as such; a wrong password still reads as the generic failure (never which field).
   */
  accountSuspended = false;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  private readonly errorHandler = inject(ErrorHandler);

  constructor(
    private actions: Actions,
    private loginRedirectService: UserRoleBasedRedirectService,
    private route: ActivatedRoute
  ) {
    super();
    this.title = 'Login';
  }

  ngOnInit(): void {
    this.buildForm();
    // Rebuild the form copy once the new language's strings have LOADED. activeLangChanged fires
    // after the load; the ChangeAppLang dispatch completes before it.
    this.translate.activeLangChanged.subscribe(() => {
      this.buildForm();
      this.cdr.markForCheck();
    });

    this.actions.pipe(ofActionCompleted(LogIn)).subscribe(({ result }) => {
      if (result.error) {
        this.onLoginFailed(result.error);
      }
    });

    this.actions.pipe(ofActionSuccessful(LogIn)).subscribe(() => {
      this.loaderService.hideLoader();
      this.blurActiveElement();
      const pushToken = this.store.selectSnapshot((state) => state.app.pushToken);
      if (pushToken !== this.currentUser.pushToken) {
        this.store.dispatch(new UpdatePushToken({ id: this.currentUser.id, pushToken }));
      }
      // Every tenant member loads their tenant now — staff work in the provider app too (the
      // owner's rule, 28 Sep 2026), and a session restore already does the same for any tenantId.
      if (this.currentUser.tenantId) {
        this.store.dispatch(new SetCurrentTenant({ id: this.currentUser.tenantId }));
      }
      const returnPath = this.route.snapshot.queryParamMap.get('return') || this.route.snapshot.queryParamMap.get('returnUrl');
      if (returnPath?.startsWith('/')) {
        this.navCtrl.navigateRoot([returnPath]);
        return;
      }
      this.loginRedirectService.redirectUser(this.appEnv, this.navCtrl, this.currentUser, this.userRoleRouteRedirectPathMappingList);
    });

    this.actions.pipe(ofActionSuccessful(SetCurrentTenant)).subscribe(() => {
      const currentTenant = this.store.selectSnapshot((state) => state.app.currentTenant);
      if (currentTenant?.supportedLanguages?.length) {
        this.store.dispatch(new SetCurrentTenantSupportedLanguages(currentTenant.supportedLanguages));
      }
    });
  }

  ionViewWillEnter(): void {
    this.isLoadingState = false;
    this.menuCtrl.enable(false);
  }

  ionViewWillLeave(): void {
    this.blurActiveElement();
    this.menuCtrl.enable(true);
    this.isLoadingState = false;
  }

  /** The shared EN/FR preference (`AppState.AppLang`). The form is rebuilt when the strings load (ngOnInit). */
  onLanguageChange(code: string): void {
    if (code !== 'en' && code !== 'fr') {
      return;
    }
    this.store.dispatch(new ChangeAppLang(code));
  }

  onSubmit(): void {
    this.loginFailed = false;
    this.accountSuspended = false;
    if (!this.form.valid) {
      // Nothing is disabled up front (as in the DS): an invalid press shows each field's own error.
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }
    this.isLoadingState = true;
    this.loaderService.showLoader(this.languageService.get('uiString.connecting'));

    const { username: phone, password } = this.form.value;
    this.store.dispatch(new SetLocalizationSettings(this.storeUtilSrv.getCountryCallingCode(this.store, phone)));
    const username = utils.isEmail(phone) ? phone : utils.formatUsernameToEmailFormat(phone, this.appDomain);
    /* The page answers a refusal itself (ofActionCompleted above); subscribing here marks the error
       HANDLED, so NGXS does not pass an expected refusal to the ErrorHandler → rollbar (N-8). */
    this.store.dispatch(new LogIn({ username, password })).subscribe({ error: (e: unknown) => this.reportSignInError(e) });
  }

  /** An expected refusal (a suspended account, a wrong password) is logged at warn; anything else is still reported. */
  reportSignInError(error: unknown): void {
    if (isExpectedSignInRefusal(error)) {
      console.warn('sign-in refused', signInErrorCode(error));
      return;
    }
    this.errorHandler.handleError(error);
  }

  /** A rejected sign-in: the generic failure, or — `auth/user-disabled` — the suspended account (kz2 M12). */
  onLoginFailed(error: unknown): void {
    this.formSubmissionErrorOccured = true;
    this.loginFailed = true;
    this.accountSuspended = (error as { code?: unknown } | null)?.code === 'auth/user-disabled';
    this.isLoadingState = false;
    this.loaderService.hideLoader();
    this.cdr.markForCheck();
  }

  onForgotPassword(): void {
    this.navCtrl.navigateForward('forgot-password');
  }

  private blurActiveElement(): void {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
  }

  /** (Re)builds the dynamic form in the active language, keeping whatever was typed. */
  private buildForm(): void {
    const previous = this.form?.getRawValue();
    this.formModel = this.commonFormModels.getLoginFormModel();
    this.modelMap = this.dynamicFormsService.getformModelMap(this.formModel);
    this.form = this.dynamicFormsService.createFormGroup(this.formModel);
    if (previous) {
      this.form.patchValue(previous);
    }
  }
}
